import React, { useState, useEffect } from 'react';
import ProductImage from './ProductImage';

interface ProductDialogProps {
  product: any;
  isOpen: boolean;
  onClose: () => void;
  onAdd: (quantity: number) => void;
  onRemove?: () => void;
  initialQuantity?: number;
  cart?: any[];
}

function ProductDialog({ product, isOpen, onClose, onAdd, onRemove, initialQuantity = 1, cart = [] }: ProductDialogProps) {
  const [quantity, setQuantity] = useState(initialQuantity);

  // Reset quantity when product changes or when initialQuantity changes
  useEffect(() => {
    setQuantity(initialQuantity);
  }, [product?.id, initialQuantity]);

  if (!isOpen) return null;

  // Calculate available stock for this dialog (actual stock minus other cart items, plus current item if editing)
  const currentCartItem = cart.find(item => item.id === product.id);
  const otherCartQuantity = cart
    .filter(item => item.id !== product.id)
    .reduce((sum, item) => sum + item.quantity, 0);
  
  // For validation, we use the actual product stock
  const maxSelectableQuantity = product.stock;

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog" onClick={e => e.stopPropagation()}>
        <div className="dialog-header">
          <h2>Agregar al Carrito</h2>
          <button className="dialog-close" onClick={onClose}>×</button>
        </div>
        <div className="dialog-content">
          <div className="dialog-product">
            <ProductImage src={product.image} alt={product.name} className="dialog-product-image" />
            <div className="dialog-product-info">
              {product.brand && <p className="dialog-product-brand">{product.brand}</p>}
              <h3>{product.name}</h3>
              <p className="dialog-product-price">${product.price.toFixed(2)}</p>
              <p className={`dialog-product-stock${product.stock === 0 ? ' out-of-stock' : ''}`}>
                Stock: {product.stock === 0 ? 'Agotado' : product.stock}
              </p>
            </div>
          </div>
          <div className="quantity-selector">
            <label>Cantidad:</label>
            <div className="quantity-controls">
              <button
                className="quantity-btn"
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                disabled={quantity <= 1 || product.stock === 0}
              >
                -
              </button>
              <span className="quantity-display">{quantity}</span>
              <button
                className="quantity-btn"
                onClick={() => setQuantity(Math.min(maxSelectableQuantity, quantity + 1))}
                disabled={quantity >= maxSelectableQuantity || product.stock === 0}
              >
                +
              </button>
              {onRemove && (
                <button
                  className="quantity-btn remove-btn"
                  title="Eliminar del carrito"
                  style={{ marginLeft: 12, color: '#e74c3c' }}
                  onClick={() => {
                    onRemove();
                  }}
                >
                  🗑️
                </button>
              )}
            </div>
            <div className="quantity-presets">
              {[5, 10, 15, 20].map(preset => (
                <button
                  key={preset}
                  className={`quantity-preset-btn${(quantity === preset) ? ' active' : ''}`}
                  onClick={() => {
                    setQuantity(Math.min(preset, maxSelectableQuantity));
                  }}
                  disabled={preset > maxSelectableQuantity || product.stock === 0}
                >
                  {preset}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="dialog-footer">
          <button className="btn-secondary" onClick={onClose}>Cancelar</button>
          <button 
            className="btn-primary" 
            onClick={() => {
              onAdd(quantity);
              onClose();
            }}
            disabled={product.stock === 0}
          >
              {product.stock === 0 ? 'Sin Stock' : 'Agregar'} ({quantity})
          </button>
        </div>
      </div>
    </div>
  );
}

export default ProductDialog; 